import { getUrl } from "aws-amplify/storage";

export function pickAvatarValue(user) {
  return (
    user?.profilePicture ||
    user?.avatar ||
    user?.image ||
    user?.profile?.profilePicture ||
    ""
  );
}

function isHttpUrl(value) {
  return /^https?:\/\//i.test(String(value || ""));
}

function isLikelyStoragePath(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return false;
  if (isHttpUrl(normalized)) return false;
  if (normalized.includes("/")) return true;
  return /\.(png|jpe?g|gif|webp|avif|svg)$/i.test(normalized);
}

// Resolving a storage-path avatar costs a presign round-trip. The community
// grid, chat lists, and chat headers mount many avatars that repeat the same
// storage paths across pages and re-renders, so without a cache every card
// issues its own presign — and revisiting a page re-resolves all of them.
// Cache resolved URLs per path (presigned URLs are stable for the signature
// lifetime, ~15 min) and de-dupe concurrent in-flight resolutions of the same
// path so a page of avatars collapses to one request per distinct image.
const PROFILE_IMAGE_URL_TTL_MS = 10 * 60_000;
const resolvedUrlCache = new Map(); // path -> { url, expiresAt }
const inFlightUrlByPath = new Map(); // path -> Promise<string>

function readCachedAvatarUrl(path) {
  const entry = resolvedUrlCache.get(path);
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    resolvedUrlCache.delete(path);
    return null;
  }
  return entry.url;
}

// Exposed for tests / explicit refresh after an avatar is replaced.
export function clearProfileImageUrlCache() {
  resolvedUrlCache.clear();
  inFlightUrlByPath.clear();
}

export async function resolveProfileImageUrlFromValue(value) {
  const avatar = String(value || "").trim();
  if (!avatar) return "";

  if (isHttpUrl(avatar)) {
    return avatar;
  }

  if (!isLikelyStoragePath(avatar)) {
    return "";
  }

  const cached = readCachedAvatarUrl(avatar);
  if (cached !== null) return cached;

  const pending = inFlightUrlByPath.get(avatar);
  if (pending) return pending;

  const resolution = (async () => {
    try {
      // validateObjectExistence is intentionally omitted: it forces an extra
      // S3 HeadObject per avatar, and the <img onError> fallback already
      // handles a missing object gracefully, so the existence pre-check is
      // redundant network cost on the display path.
      const urlResult = await getUrl({ path: avatar });
      const url = urlResult?.url?.toString?.() || "";
      if (url) {
        resolvedUrlCache.set(avatar, {
          url,
          expiresAt: Date.now() + PROFILE_IMAGE_URL_TTL_MS,
        });
      }
      return url;
    } catch (error) {
      console.warn("Profile picture path missing or unavailable:", avatar, error);
      return "";
    } finally {
      inFlightUrlByPath.delete(avatar);
    }
  })();

  inFlightUrlByPath.set(avatar, resolution);
  return resolution;
}

export async function resolveProfileImageUrl(user) {
  return resolveProfileImageUrlFromValue(pickAvatarValue(user));
}
